import { cn } from "@/lib/utils";

type RouteLoadingShellProps = {
  variant?: "admin-table" | "chat" | "dashboard" | "model" | "shared-chat";
};

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn("rounded bg-muted", className)} />;
}

function ChatLoading({ shared = false }: { shared?: boolean }) {
  return (
    <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground">
      <span className="sr-only">Loading</span>
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
        <SkeletonBlock className="h-8 w-44" />
        <div className="flex gap-2">
          <SkeletonBlock className="h-8 w-8 rounded-md" />
          <SkeletonBlock className="h-8 w-8 rounded-md" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 px-4 py-8">
            {(shared ? [0, 1, 2, 3] : [0, 1, 2]).map((item) => (
              <div className="space-y-2" key={item}>
                <SkeletonBlock className="h-4 w-28" />
                <SkeletonBlock
                  className={cn("h-20", item % 2 === 0 ? "w-5/6" : "w-full")}
                />
              </div>
            ))}
          </div>
          {!shared ? (
            <div className="border-t border-border p-4">
              <SkeletonBlock className="mx-auto h-24 w-full max-w-3xl rounded-lg" />
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function ModelLoading() {
  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <span className="sr-only">Loading</span>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex gap-2">
            <SkeletonBlock className="h-6 w-24 rounded-full" />
            <SkeletonBlock className="h-6 w-16 rounded-full" />
          </div>
          <SkeletonBlock className="h-9 w-full max-w-xl" />
          <SkeletonBlock className="h-4 w-full max-w-2xl" />
          <SkeletonBlock className="h-4 w-80 max-w-full" />
        </div>
        <SkeletonBlock className="h-9 w-32" />
      </div>
      <div className="my-8 h-px bg-border" />
      <div className="mb-6 flex flex-wrap gap-2">
        {[0, 1, 2, 3].map((item) => (
          <SkeletonBlock className="h-9 w-28 rounded-md" key={item} />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <SkeletonBlock className="h-80 rounded-xl" />
        <SkeletonBlock className="h-80 rounded-xl" />
      </div>
    </main>
  );
}

function AdminTableLoading() {
  return (
    <main className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-8 sm:px-6 lg:px-8">
      <span className="sr-only">Loading</span>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <SkeletonBlock className="h-8 w-64" />
          <SkeletonBlock className="h-4 w-96 max-w-full" />
        </div>
        <SkeletonBlock className="h-9 w-36" />
      </div>
      <div className="rounded-lg border border-border/70">
        <div className="grid grid-cols-5 gap-3 border-b border-border/70 p-4">
          {[0, 1, 2, 3, 4].map((item) => (
            <SkeletonBlock className="h-4" key={item} />
          ))}
        </div>
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div className="grid grid-cols-5 gap-3 border-b border-border/40 p-4" key={row}>
            {[0, 1, 2, 3, 4].map((cell) => (
              <SkeletonBlock className="h-5" key={cell} />
            ))}
          </div>
        ))}
      </div>
    </main>
  );
}

function DashboardLoading() {
  return (
    <main className="relative min-h-full min-w-0 overflow-x-clip">
      <span className="sr-only">Loading dashboard</span>
      <section className="relative px-4 pt-12 pb-8 sm:px-6 lg:px-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -top-10 left-[18%] h-56 w-56 rounded-full bg-chart-2/10 blur-3xl" />
          <div className="absolute -top-16 right-[16%] h-56 w-56 rounded-full bg-chart-3/10 blur-3xl" />
        </div>
        <div className="relative mx-auto flex max-w-5xl flex-col items-center">
          <SkeletonBlock className="h-14 w-72 max-w-full rounded-lg" />
          <SkeletonBlock className="mt-6 h-12 w-96 max-w-full" />
          <SkeletonBlock className="mt-4 h-6 w-52 max-w-full" />
        </div>
      </section>
      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <SkeletonBlock className="h-40 rounded-xl" />
          {[0, 1].map((section) => (
            <div className="space-y-4" key={section}>
              <div className="flex items-center gap-3">
                <SkeletonBlock className="h-4 w-28" />
                <SkeletonBlock className="h-px flex-1 rounded-none" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((card) => (
                  <SkeletonBlock className="h-48 rounded-xl" key={card} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

export function RouteLoadingShell({ variant = "model" }: RouteLoadingShellProps) {
  if (variant === "chat") return <ChatLoading />;
  if (variant === "shared-chat") return <ChatLoading shared />;
  if (variant === "admin-table") return <AdminTableLoading />;
  if (variant === "dashboard") return <DashboardLoading />;
  return <ModelLoading />;
}
